import { AsyncPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from '@core/services/config.service';
import { DownloadLinkHandlerService } from '@core/services/download-link-handler.service';
import { EditModeService } from '@core/services/edit-mode.service';
import { ThemeService } from '@core/services/theme.service';
import { PAGE_TITLES, usePageTitle } from '@core/utils/page-title.util';
import { PagePreviewBaseComponent } from '@feature/preview-management/components/page-preview-base/page-preview-base.component';
import { ValidatorComponent } from '@feature/survey-management/components/validator/validator.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Icon } from '@shared/components/icon/icon.enum';
import { EventCollectEmailComponent } from '@shared/models/components/event-collect-email';
import { EventReceiptComponent } from '@shared/models/components/event-receipt';
import { ValidatorType } from '@shared/models/enums/validator-type.enum';
import { ErrorState } from '@shared/state/error.state';
import { catchError, Observable } from 'rxjs';
import { ChooseLanguageComponent } from '../../../../core/components/choose-language/choose-language.component';
import { NavigationComponent } from '../../../../core/components/navigation/navigation.component';
import { CanComponentDeactivate } from '../../../../core/guards/can-deactivate.guard';
import { CustomTranslateLoader } from '../../../../core/i18n/custom-translate-loader';
import { AnswerModuleService } from '../../../../core/services/answer-module.service';
import { RedirectService } from '../../../../core/services/redirect.service';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { LayoutComponent } from '../../../../shared/components/layout/layout.component';
import { LanguageSlotDirective } from '../../../../shared/directives/language-slot.directive';
import { AnswerModuleResponse } from '../../../../shared/models/answer-module-response';
import { ChooseLanguage } from '../../../../shared/models/choose-language';
import { EventChooseLanguageComponent } from '../../../../shared/models/components/event-choose-language';
import { EventInformationComponent } from '../../../../shared/models/components/event-information-component';
import { InformationRequest } from '../../../../shared/models/information-request';
import { InformationView } from '../../../../shared/models/views-models/information-view';
import { SafeHtmlPipe } from '../../../../shared/pipes/safe-html.pipe';
import { TranslationTagsPipe } from '../../../../shared/pipes/translation-tags.pipe';
import {
  createLanguageObject,
  mapLanguageOptions,
  updateTextDirection,
} from '../../../../shared/utils/helper.util';
import {
  cleanupViewportHeight,
  setupViewportHeight,
} from '../../../../shared/utils/viewport.util';

@Component({
  selector: 'app-information-page',
  imports: [
    NavigationComponent,
    LayoutComponent,
    LanguageSlotDirective,
    FormsModule,
    TranslateModule,
    ChooseLanguageComponent,
    SafeHtmlPipe,
    TranslationTagsPipe,
    AsyncPipe,
    ValidatorComponent,
    ReactiveFormsModule,
    IconComponent,
    ButtonComponent,
  ],
  templateUrl: './information-page.component.html',
  styleUrl: './information-page.component.scss',
})
export class InformationPageComponent
  extends PagePreviewBaseComponent
  implements OnInit, CanComponentDeactivate, OnDestroy
{
  private setPageTitle = usePageTitle();
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    protected override redirectService: RedirectService,
    private answerService: AnswerModuleService,
    private translateService: TranslateService,
    private translateLoader: CustomTranslateLoader,
    protected override themeService: ThemeService,
    private configService: ConfigService,
    private downloadLinkHandler: DownloadLinkHandlerService,
    private editModeService: EditModeService,
  ) {
    super(redirectService, themeService);
    this.useClientValidation = this.configService.get('USE_CLIENT_VALIDATION');

    const emailWithTldPattern = /^.+@.+\..+$/;

    this.emailReceiptControl = new FormControl('', [
      Validators.email,
      Validators.pattern(emailWithTldPattern),
    ]);

    this.emailCollectControl = new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.pattern(emailWithTldPattern),
    ]);
  }

  @HostListener('window:popstate')
  onPopState() {
    history.pushState(null, '');
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    this.downloadLinkHandler.handleClick(event);
  }

  store = inject(Store);
  error$ = this.store.select(ErrorState.getErrors);
  validator = ValidatorType;
  useClientValidation: boolean;
  informationView?: InformationView;
  hiddenIdentityResponse: boolean | null = false;
  hiddenIdentityRequest: boolean = false;
  emailReceiptControl!: FormControl;
  emailCollectControl!: FormControl;
  chooseLanguageComponent?: EventChooseLanguageComponent;
  informationPageTranslationkeys: string[] = [
    'welcome.hiddenidentity.',
    'validation.',
    'navigation.',
    'information.',
  ];
  informationPageComponent?: EventInformationComponent;
  languageOptions: { value: string; label: string; src: string }[] = [];
  selectedLanguage: string = '';
  Icon = Icon;

  showEmailReceiptInput: boolean = false;
  emailReceiptSent: boolean = false;
  emailReceiptUrl: string = '';
  emailReceiptComponent?: EventReceiptComponent;
  emailCollectComponent?: EventCollectEmailComponent;
  private readonly destroyRef = inject(DestroyRef);
  private beforeUnloadHandler = this.onBeforeUnload.bind(this);

  protected override onResponseSet(value: AnswerModuleResponse): void {
    if (this.previewMode && value?.view?.type === 'INFORMATION') {
      this.initInformationComponents(value);
    }
  }

  onBeforeUnload(event: Event): void {
    // Don't show warning for download links
    if (this.downloadLinkHandler.isDownload()) {
      this.downloadLinkHandler.reset();
      return;
    }

    if (
      this.response?.view?.data?.componentType !== 'THANKYOU_INFORMATION' &&
      this.response?.responseId !== '00000000-0000-0000-0000-000000000000'
    ) {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    this.downloadLinkHandler.reset();
    cleanupViewportHeight();
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    return this.allowNavigation;
  }

  ngOnInit(): void {
    this.setPageTitle(
      PAGE_TITLES.INFORMATION.key,
      PAGE_TITLES.INFORMATION.fallback,
    );

    // Set viewport height for mobile browsers
    setupViewportHeight();

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    } else {
      this.informationPageTranslationkeys.push('preview.');
    }

    if (!this.previewMode) {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const response = history.state.data as AnswerModuleResponse;

        // Redirect to error page if no valid response data
        if (!response && !this.previewMode) {
          this.allowNavigation = true;
          this.router.navigate(['/error-page', '404']);
          return;
        }

        if (!this.previewMode && response) {
          this.response = response;
        }

        if (!this.response) {
          return;
        }

        this.selectedLanguage = this.response.language;
        if (
          this.response.view.data.componentType === 'THANKYOU_INFORMATION' &&
          this.response.view.type === 'INFORMATION'
        ) {
          this.emailReceiptComponent = this.response.view.data.components.find(
            (i) => i.$type === '@event-receipt',
          );
          if (this.emailReceiptComponent) {
            this.emailReceiptControl.setValue(this.emailReceiptComponent.email);
          }
        }
        this.initInformationComponents(
          response,
          this.informationPageTranslationkeys,
        );
      });
    const redirectInfo =
      this.informationPageComponent?.redirect ||
      this.informationView?.data.redirect;
    if (redirectInfo) {
      this.redirect(redirectInfo.url, redirectInfo.timeout);
    }

    updateTextDirection(this.selectedLanguage);
  }

  initInformationComponents(
    response: AnswerModuleResponse,
    translationKeys: string[] = [],
  ) {
    if (response && response.view.type === 'INFORMATION') {
      translationKeys.push('navigation.');
      if (response.view.data.componentType === 'THANKYOU_INFORMATION') {
        this.allowNavigation = false;
        translationKeys.push('thankyou.defaulttext');
      }
      if (response.view.data.componentType === 'GDPR_DECLINED_INFORMATION') {
        this.allowNavigation = false;
        translationKeys.push('gdpr.statement.notparticipate.');
      }
      if (response.view.data.componentType === 'EDIT_REPONSE_COMPLETED') {
        this.allowNavigation = false;
        this.editModeService.clearEditMode();
        translationKeys.push('editresponse.');
      }

      this.informationView = response.view;

      const hasRenderableInformationContent =
        response.view.data.components?.some(
          (component) =>
            component.componentType === 'INFORMATION' &&
            !this.isHtmlEffectivelyEmpty(component.message),
        ) ?? false;
      const hasNonInformationComponents =
        response.view.data.components?.some(
          (component) => component.componentType !== 'INFORMATION',
        ) ?? false;

      if (
        response.view.data.componentType === 'INITIAL_INFORMATION' &&
        !response.view.data.translationKeyBody &&
        this.isHtmlEffectivelyEmpty(response.view.data.message) &&
        !hasRenderableInformationContent &&
        !hasNonInformationComponents
      ) {
        this.next();
        return;
      }

      this.informationPageComponent =
        this.informationView.data.components?.find(
          (i) => i.$type === '@event-information',
        );
      this.emailCollectComponent = this.informationView.data.components?.find(
        (i) => i.$type === '@event-collect-email',
      );
      this.chooseLanguageComponent = this.informationView.data.components?.find(
        (i) => i.$type === '@event-choose-language',
      );

      if (this.chooseLanguageComponent) {
        this.languageOptions = mapLanguageOptions(this.chooseLanguageComponent);
      }
      if (response.view.data.components?.length) {
        for (
          let index = 0;
          index < response.view.data.components.length;
          index++
        ) {
          const component = response.view.data.components[index];
          if (component.componentType === 'HIDDEN_INFORMATION') {
            this.hiddenIdentityResponse = component.hidden;
            if (!this.chooseLanguageComponent) {
              translationKeys.push('welcome.hiddenidentity.');
            }
            break;
          } else if (component.componentType === 'SEND_RECEIPT') {
            translationKeys.push(
              'thankyou.sendreceipt.',
              'validation.responsereceipt.',
              'thankyou.defaulttext',
            );
            this.showEmailReceiptInput = true;
            this.emailReceiptUrl = component.callbackUrl;
          }
        }
      }

      if (!this.chooseLanguageComponent) {
        this.selectedLanguage = this.response.language;

        this.translateLoader
          .getTranslation(
            this.selectedLanguage,
            translationKeys,
            this.response.fallbackLanguage,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((translation) => {
            this.translateService.setTranslation(
              this.selectedLanguage,
              translation,
              true,
            );
            this.translateService.use(this.selectedLanguage);
          });
      }
    }
  }

  private isHtmlEffectivelyEmpty(html: string | null | undefined): boolean {
    if (!html) return true;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    if ((tmp.textContent ?? '').trim() !== '') {
      return false;
    }

    const ignorableFormattingTags = new Set([
      'SPAN',
      'STRONG',
      'B',
      'EM',
      'I',
      'U',
      'S',
      'STRIKE',
      'P',
      'DIV',
      'BR',
      'FONT',
      'SUB',
      'SUP',
    ]);

    const elements = tmp.querySelectorAll('*');
    for (const element of Array.from(elements)) {
      if (!ignorableFormattingTags.has(element.tagName)) {
        return false;
      }
    }

    return true;
  }

  redirect(url: string, seconds: number) {
    setTimeout(() => {
      window.location.href = url;
    }, seconds * 1000);
  }

  next() {
    if (this.response?.responseId) {
      const requestBody: InformationRequest[] = [];
      if (
        this.hiddenIdentityResponse === null &&
        (!this.useClientValidation && !this.hiddenIdentityRequest) === false
      ) {
        requestBody.push({
          type: 'HIDDEN',
          data: {
            $type: 'hiddenResponseData',
            hidden: this.hiddenIdentityRequest,
          },
          responseId: this.response?.responseId,
        });
      }
      if (this.emailCollectComponent) {
        this.emailCollectControl.markAsTouched();
        if (this.emailCollectControl.invalid) {
          document
            .getElementById('email-collect')
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        requestBody.push({
          type: 'COLLECT_EMAIL',
          data: {
            $type: 'emailResponseData',
            email: this.emailCollectControl.value,
          },
          responseId: this.response?.responseId,
        });
      }
      this.answerService
        .getNextState(
          this.response?.responseId,
          this.response?.stateId,
          requestBody,
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((response) => {
          if (response.body) {
            this.handlePreviewMode(response.body);
          }
        });
    }
  }

  setLanguage({ language, calledWithInitialLanguage }: ChooseLanguage) {
    this.selectedLanguage = language;

    updateTextDirection(language);

    if (!calledWithInitialLanguage) {
      this.postLanguage();
    }
  }

  postLanguage() {
    if (
      this.response?.responseId &&
      this.response.responseId !== '00000000-0000-0000-0000-000000000000'
    ) {
      const languageRequest = createLanguageObject(
        this.response?.responseId,
        this.selectedLanguage,
      );
      this.answerService
        .postLanguage(
          this.response.responseId,
          this.response.stateId,
          languageRequest,
        )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(() => {
            this.allowNavigation = true;
            return [];
          }),
        )
        .subscribe((res) => {
          if (res && res.body?.view.type === 'INFORMATION') {
            this.response = res.body;
            this.informationView = res.body.view;
          }
        });
    }
  }

  onSendEmailSubmit() {
    if (this.emailReceiptControl.valid && this.emailReceiptControl.value) {
      this.answerService
        .postCallback(
          this.emailReceiptUrl,
          `"${this.emailReceiptControl.value.toString()}"`,
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            if (response.ok) {
              this.showEmailReceiptInput = false;
              this.emailReceiptSent = true;
            }
          },
        });
    } else {
      this.emailReceiptControl?.markAsTouched();
    }
  }

  hasHiddenIdentityError(): boolean {
    const errors = this.store.selectSnapshot(ErrorState.getErrors);
    return (
      errors?.some(
        (e) => e.translationKey === 'validation.general.missinghiddenanswer',
      ) ?? false
    );
  }

  isHiddenIdentityRequired(): boolean {
    return this.hiddenIdentityResponse === null;
  }

  getHiddenIdentityAriaDescribedBy(): string {
    const baseDescription = 'hiddenIdentityDescription';
    if (this.hasHiddenIdentityError()) {
      return `${baseDescription} hiddenIdentityError`;
    }
    return baseDescription;
  }

  getEmailReceiptAriaDescribedBy(): string | null {
    if (this.emailReceiptControl.invalid && this.emailReceiptControl.touched) {
      return 'email-receipt-error';
    }
    const errors = this.store.selectSnapshot(ErrorState.getErrors);
    if (
      errors?.some(
        (e) =>
          e.translationKey ===
          'validation.responsereceipt.receipthasalreadybeensent',
      )
    ) {
      return 'email-receipt-sent-error';
    }
    return null;
  }

  getEmailCollectAriaDescribedBy(): string | null {
    if (!this.emailCollectControl.value && this.emailCollectControl.touched) {
      return 'email-collect-empty-error';
    }
    if (this.emailCollectControl.invalid && this.emailCollectControl.touched) {
      return 'email-collect-invalid-error';
    }
    return null;
  }
}
